import type { Issue } from "@qualtio-workhub/contracts";
import { Avatar, Badge, Button, Select, Table } from "@qualtio-workhub/ui";
import { useRouter } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, Bot, ExternalLink } from "lucide-react";
import type { DragEvent } from "react";
import type { Agent, ContextUser } from "../../shared/api/client";
import { useI18n } from "../../shared/i18n/provider";
import { type IssueListSearch, type IssueSort, issueListSearchParams } from "./issue-list-model";
import {
  type IssueStatus,
  priorityLabelKeys,
  statusChangePayload,
  statusLabelKeys,
  statusOptions,
  statusTone,
  typeLabelKeys,
} from "./model";

export function StatusBadge({ status }: { status: IssueStatus }) {
  const { t } = useI18n();
  return (
    <Badge className={`status-badge status-badge-${status}`} tone={statusTone[status]}>
      {t(statusLabelKeys[status])}
    </Badge>
  );
}

export function PriorityBadge({ priority }: { priority: Issue["priority"] }) {
  const { t } = useI18n();
  const tone = priority === "urgent" || priority === "high" ? "danger" : "neutral";
  return (
    <Badge className={`priority-badge priority-badge-${priority}`} tone={tone}>
      {t(priorityLabelKeys[priority])}
    </Badge>
  );
}

export function IssueKey({ issue }: { issue: Issue }) {
  return <span className="issue-key">{issue.key}</span>;
}

export function IssueTable({
  issues,
  compact = false,
  users,
  agents = [],
  search = {},
  onSort,
  scrollable = false,
}: {
  issues: Issue[];
  compact?: boolean;
  users?: Pick<ContextUser, "id" | "name">[];
  agents?: Pick<Agent, "id" | "name">[];
  search?: IssueListSearch;
  onSort?: (sort: IssueSort) => void;
  scrollable?: boolean;
}) {
  const { t } = useI18n();
  const params = issueListSearchParams(search);
  const activeSort = search.sort ?? "updated";
  const direction = search.direction ?? "desc";
  const sortIcon =
    direction === "asc" ? (
      <ArrowUp aria-hidden="true" size={12} />
    ) : (
      <ArrowDown aria-hidden="true" size={12} />
    );

  function sortableHeading(sort: IssueSort, label: string) {
    return onSort ? (
      <Button size="sm" variant="ghost" onClick={() => onSort(sort)}>
        {label}
        {activeSort === sort ? sortIcon : null}
      </Button>
    ) : (
      label
    );
  }

  return (
    <div
      className={`issue-table-shell${compact ? " issue-table-shell-compact" : ""}${scrollable ? " issue-table-shell-scrollable" : ""}`}
    >
      <Table className="issue-table">
        <thead>
          <tr>
            <th>{t("issue.field.issue")}</th>
            <th>{t("issue.field.status")}</th>
            <th
              aria-sort={
                onSort
                  ? activeSort === "priority"
                    ? direction === "asc"
                      ? "ascending"
                      : "descending"
                    : "none"
                  : undefined
              }
            >
              {sortableHeading("priority", t("issue.field.priority"))}
            </th>
            {users ? <th>{t("issue.field.assigneeUser")}</th> : null}
            {!compact ? <th>{t("issue.field.type")}</th> : null}
            <th
              aria-sort={
                onSort
                  ? activeSort === "updated"
                    ? direction === "asc"
                      ? "ascending"
                      : "descending"
                    : "none"
                  : undefined
              }
            >
              {sortableHeading("updated", t("issue.field.updated"))}
            </th>
          </tr>
        </thead>
        <tbody>
          {issues.map((issue) => {
            const agent = agents.find((item) => item.id === issue.assigneeAgentId);
            const owner = users?.find((item) => item.id === issue.assigneeUserId);
            const ownerName = issue.assigneeUserId
              ? (owner?.name ?? t("issue.owner.unknown"))
              : (agent?.name ??
                (issue.assigneeAgentId ? t("issue.field.agent") : t("issue.unassigned")));
            return (
              <tr className="issue-row" key={issue.id}>
                <td className="issue-main-cell" data-label={t("issue.field.issue")}>
                  <a
                    className="issue-title-cell issue-title-link"
                    href={`/issues/${issue.key}${params ? `?${params}` : ""}`}
                  >
                    <IssueKey issue={issue} />
                    <span>{issue.title}</span>
                  </a>
                </td>
                <td className="issue-status-cell" data-label={t("issue.field.status")}>
                  <StatusBadge status={issue.status} />
                </td>
                <td className="issue-priority-cell" data-label={t("issue.field.priority")}>
                  <PriorityBadge priority={issue.priority} />
                </td>
                {users ? (
                  <td className="issue-owner-cell" data-label={t("issue.field.assigneeUser")}>
                    <span className="issue-owner">
                      {issue.assigneeUserId || issue.assigneeAgentId ? (
                        <Avatar aria-hidden="true" size="sm">
                          {!issue.assigneeUserId && issue.assigneeAgentId ? (
                            <Bot size={12} />
                          ) : (
                            ownerName.slice(0, 1)
                          )}
                        </Avatar>
                      ) : null}
                      <span>{ownerName}</span>
                    </span>
                  </td>
                ) : null}
                {!compact ? (
                  <td className="issue-type-cell" data-label={t("issue.field.type")}>
                    {t(typeLabelKeys[issue.type])}
                  </td>
                ) : null}
                <td className="issue-date-cell" data-label={t("issue.field.updated")}>
                  {formatDate(issue.updatedAt)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

export function IssueCard({
  issue,
  dragging = false,
  onDragEnd,
  onDragStart,
  onStatusChange,
  recentlyMoved = false,
}: {
  issue: Issue;
  dragging?: boolean;
  onDragEnd?: () => void;
  onDragStart?: (issue: Issue, event: DragEvent<HTMLElement>) => void;
  onStatusChange?: (issue: Issue, status: IssueStatus) => void;
  recentlyMoved?: boolean;
}) {
  const router = useRouter();
  const { t } = useI18n();

  return (
    <article
      className="board-card"
      data-dragging={dragging ? "true" : undefined}
      data-issue-key={issue.key}
      data-issue-status={issue.status}
      data-recently-moved={recentlyMoved ? "true" : undefined}
      draggable={onDragStart !== undefined}
      onDragEnd={onDragEnd}
      onDragStart={(event) => onDragStart?.(issue, event)}
    >
      <button
        className="board-card-link"
        type="button"
        onClick={() =>
          router.navigate({ to: "/issues/$issueKey", params: { issueKey: issue.key } })
        }
      >
        <span>
          <IssueKey issue={issue} />
          {issue.title}
        </span>
        <ExternalLink aria-hidden="true" size={14} />
      </button>
      <div className="issue-card-meta">
        <PriorityBadge priority={issue.priority} />
        <span>{t(typeLabelKeys[issue.type])}</span>
      </div>
      {onStatusChange ? (
        <Select
          aria-label={t("issue.action.changeStatus")}
          value={issue.status}
          onChange={(event) => onStatusChange(issue, event.currentTarget.value as IssueStatus)}
        >
          {statusOptions.map((status) => (
            <option key={status} value={status}>
              {t(statusLabelKeys[status])}
            </option>
          ))}
        </Select>
      ) : null}
    </article>
  );
}

export function formatDate(value: string, timeZone?: string): string {
  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    timeZone,
  }).format(new Date(value));
}

export { statusChangePayload };
